import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

/** Restore the session from the HttpOnly refresh cookie on boot. */
let restorePromise: Promise<boolean> | null = null;
export const tryRestoreSession = (): Promise<boolean> => {
  restorePromise ??= (async () => {
    try {
      const { data } = await axios.post<{ accessToken: string; role: string }>(
        `${BASE_URL}/auth/refresh`, {}, { withCredentials: true },
      );
      useAuthStore.getState().setAuth(data.accessToken, data.role);
      return true;
    } catch {
      return false;
    }
  })();
  return restorePromise;
};

// Every operator request carries the Bearer token + the explicit admin-context header.
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Admin-Context'] = 'true';
  return config;
});

// Auto-refresh once on 401, else drop to the login screen.
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status !== 401 || original._retry) return Promise.reject(error);
    original._retry = true;
    try {
      const { data } = await axios.post<{ accessToken: string; role: string }>(
        `${BASE_URL}/auth/refresh`, {}, { withCredentials: true },
      );
      useAuthStore.getState().setAuth(data.accessToken, data.role);
      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return apiClient(original);
    } catch (err) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      return Promise.reject(err);
    }
  },
);
